import React, { useEffect, useState } from 'react';
import { fetchEmailTemplates, updateEmailTemplate } from '../../api';
import authService from '../../../services/authService';

const authHeaders = async (extra = {}) => {
  const idToken = await authService.getIdToken();
  return { ...extra, Authorization: `Bearer ${idToken}` };
};

const TemplatesTab = () => {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Load templates on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchEmailTemplates();

        // Normalise: ensure each template is an object with subject / html / text
        const normalised = Object.fromEntries(
          Object.entries(result || {}).map(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
              return [key, { subject: '', html: '', text: '', ...value }];
            }
            // legacy single-string template becomes HTML body
            return [key, { subject: '', html: String(value), text: '' }];
          })
        );

        setTemplates(normalised);
      } catch (err) {
        console.error('Failed to fetch templates', err);
        setMessage({ type: 'error', text: 'Failed to load templates' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChange = (name, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        [field]: value
      }
    }));
  };

  const handleSave = async (name) => {
    setSaving(true);
    try {
      await updateEmailTemplate(name, templates[name]);
      setMessage({ type: 'success', text: 'Template saved' });
    } catch (err) {
      console.error('Save template failed', err);
      setMessage({ type: 'error', text: 'Save failed' });
    } finally {
      setSaving(false);
      // Hide message after 3 s
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleDownloadAll = () => {
    try {
      const blob = new Blob([JSON.stringify(templates, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'email-templates.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
      setMessage({ type: 'error', text: 'Download failed' });
    }
  };

  const handleUploadAll = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      // Sanitize: strip BOM and trailing commas (common export issues)
      const cleaned = text
        .replace(/^\uFEFF/, '')
        .replace(/,\s*([}\]])/g, '$1');
      const parsed = JSON.parse(cleaned);
      // Basic validation: ensure object of templates
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Invalid JSON structure');
      }
      // Normalise to subject/html/text per existing logic
      const normalised = Object.fromEntries(
        Object.entries(parsed).map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return [key, { subject: '', html: '', text: '', ...value }];
          }
          return [key, { subject: '', html: String(value), text: '' }];
        })
      );
      setTemplates(normalised);
      setMessage({ type: 'success', text: 'Templates loaded. Click Save All to upload.' });
    } catch (err) {
      console.error('Upload parse failed', err);
      setMessage({ type: 'error', text: 'Invalid JSON file' });
    } finally {
      event.target.value = '';
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const headers = await authHeaders({ 'Content-Type': 'application/json' });
      // Backend supports replacing entire blob when body is an object (no templateName)
      const response = await fetch(`https://uni-func-pd-thrive360.azurewebsites.net/api/blob/${encodeURIComponent('email-templates')}/${encodeURIComponent('email-templates.json')}` , {
        method: 'PUT',
        headers,
        body: JSON.stringify(templates),
        mode: 'cors'
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      setMessage({ type: 'success', text: 'All templates saved' });
    } catch (err) {
      console.error('Save all failed', err);
      setMessage({ type: 'error', text: 'Save all failed' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (loading) {
    return <p className="text-center">Loading templates…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <button
          className="bg-gray-600 hover:bg-gray-700 text-white py-1 px-3 rounded"
          onClick={handleDownloadAll}
        >
          Download JSON
        </button>
        <label className="bg-gray-600 hover:bg-gray-700 text-white py-1 px-3 rounded cursor-pointer">
          Upload JSON
          <input type="file" accept="application/json" className="hidden" onChange={handleUploadAll} />
        </label>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded disabled:opacity-50"
          disabled={saving}
          onClick={handleSaveAll}
        >
          {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>
      {message && (
        <div
          className={`p-2 rounded text-sm ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
        >
          {message.text}
        </div>
      )}
      {Object.keys(templates).length === 0 && (
        <p className="text-gray-500">No templates found.</p>
      )}
      {Object.entries(templates).map(([name, content]) => (
        <div key={name} className="bg-gray-50 p-4 rounded-lg border space-y-3">
          <h4 className="font-medium text-gray-900 capitalize">{name.replace(/([A-Z])/g, ' $1')}</h4>

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={content.subject || ''}
              onChange={(e) => handleChange(name, 'subject', e.target.value)}
            />
          </div>

          {/* HTML body */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">HTML body (editable)</label>
            <textarea
              className="w-full p-2 border rounded font-mono text-xs"
              rows={10}
              value={content.html || ''}
              onChange={(e) => handleChange(name, 'html', e.target.value)}
            />
            <label className="block text-xs font-medium text-gray-700 mb-1 mt-2">Preview</label>
            <div
              className="border p-4 rounded bg-white overflow-auto"
              dangerouslySetInnerHTML={{ __html: content.html || '' }}
            />
          </div>

          <button
            className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded"
            disabled={saving}
            onClick={() => handleSave(name)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      ))}
    </div>
  );
};

export default TemplatesTab; 